// ── Lógica pura del recuadro "Documentos Belray" (extraída para testear) ────────

import { normMatch } from './autoMatchBelray';

export interface RequisitoBelray {
  prereqId: number | string | null;
  nombre: string;
  tipo: string;
  cubierto: boolean;
  vencido?: boolean;
  required?: boolean;
  documentoId?: number | string | null;
  documentoNombre?: string | null;
  // El requisito se dejó en blanco a propósito (rechazo manual en el backend), no es
  // simplemente "nunca se subió nada".
  rechazado?: boolean;
}

// Normaliza la respuesta de cobertura Belray a la lista de requisitos del recuadro:
// cubiertos primero (✓/⚠), luego faltantes (○).
export const mapRequisitosBelray = (belrayPrereqData: any): RequisitoBelray[] => {
  const d: any = belrayPrereqData;
  if (!d) return [];
  const cub = Array.isArray(d.prerequisitos_cubiertos) ? d.prerequisitos_cubiertos : [];
  const falt = Array.isArray(d.documentos_faltantes) ? d.documentos_faltantes : [];
  const cubiertos: RequisitoBelray[] = cub.map((p: any) => ({
    prereqId: p.prerequisito_id,
    nombre: p.prerequisito_tipo || p.nombre || p.prerequisito_descripcion || 'Prerrequisito',
    tipo: p.prerequisito_tipo || p.nombre,
    cubierto: true,
    vencido: p.documento_vencido || false,
    documentoId: p.documento_id ?? null,
    documentoNombre: p.documento_tipo_normalizado || p.documento_tipo_original || null,
  }));
  const faltantes: RequisitoBelray[] = falt.map((x: any) => ({
    prereqId: x.prerequisito_id ?? x.id ?? null,
    nombre: x.label || x.nombre || x.value || x.tipo_documento || 'Documento',
    tipo: x.tipo_documento || x.nombre || x.tipo,
    cubierto: false,
    required: x.required ?? x.es_obligatorio ?? true,
    rechazado: x.rechazado === true,
  }));
  return [...cubiertos, ...faltantes];
};

// Resuelve el documento real (de la lista de documentos de la persona) que cubre un
// requisito Belray, para poder ofrecer Ver/Editar/Eliminar sobre él.
//
// La búsqueda por `documento_id` sola no basta: la cobertura del backend a veces no
// devuelve `documento_id`, y la lista local viene deduplicada por nombre+tipo, así que
// el id referenciado puede no estar presente. Sin este fallback la fila queda "cubierta"
// pero sin ningún botón de acción (el caso reportado con IRL/ODI).
export const resolveDocumentoRequisito = (
  r: RequisitoBelray | null | undefined,
  documentos: any[] | null | undefined,
): any | null => {
  if (!r?.cubierto) return null;
  const docs = Array.isArray(documentos) ? documentos : [];
  if (docs.length === 0) return null;

  // 1) Por id explícito (camino normal).
  if (r.documentoId != null && String(r.documentoId) !== '') {
    const target = String(r.documentoId);
    const byId = docs.find((d: any) => String(d?.id) === target || String(d?.documento_id) === target);
    if (byId) return byId;
  }

  // 2) Por el nombre/tipo de documento que reporta la cobertura.
  const nombreCobertura = normMatch(r.documentoNombre || '');
  if (nombreCobertura) {
    const byNombre = docs.find((d: any) =>
      [d?.nombre_documento, d?.nombre_original, d?.nombre_archivo, d?.tipo_documento, d?.tipo_normalizado]
        .some((v: any) => normMatch(v) === nombreCobertura));
    if (byNombre) return byNombre;
  }

  // 3) Por el tipo del propio prerrequisito.
  const tipoReq = normMatch(r.tipo || r.nombre || '');
  if (tipoReq) {
    const byTipo = docs.find((d: any) =>
      [d?.tipo_documento, d?.tipo_normalizado, d?.nombre_documento].some((v: any) => normMatch(v) === tipoReq));
    if (byTipo) return byTipo;
  }

  return null;
};

// Completa la respuesta de cobertura con los requisitos que el cliente tiene
// configurados pero que el detalle no mencionó (ni cubiertos ni faltantes). Sin esto el
// checklist muestra menos requisitos de los que el cliente realmente exige.
//
// `detalle` es la respuesta de getPrerequisitosDetailByRut; `configurados` la de
// getClientePrerrequisitosByClienteId. Devuelve un objeto con la misma forma que
// `detalle`, apto para mapRequisitosBelray.
//
// OJO: PersonalDetailModal tiene una copia inline de esta lógica en `fetchMergedPrereqs`
// (~línea 791). Debería adoptar este helper, pero ese archivo tiene cambios sin commitear
// y no se tocó para no pisarlos.
export const mergeRequisitosConfigurados = (detalle: any, configurados: any[] | null | undefined): any => {
  const base: any = detalle && !Array.isArray(detalle) ? detalle : {};
  const all = Array.isArray(configurados) ? configurados : [];
  if (all.length === 0) return base;

  const faltantes: any[] = Array.isArray(base.documentos_faltantes) ? base.documentos_faltantes : [];
  const cubiertos: any[] = Array.isArray(base.prerequisitos_cubiertos) ? base.prerequisitos_cubiertos : [];
  const knownIds = new Set([
    ...faltantes.map((f: any) => String(f.prerequisito_id ?? f.id ?? '')),
    ...cubiertos.map((c: any) => String(c.prerequisito_id ?? '')),
  ]);
  const missing = all.filter((p: any) => p.id && !knownIds.has(String(p.id)));
  if (missing.length === 0) return base;

  const extraFaltantes = missing.map((p: any) => {
    // V2 usa `nombre`, legacy usa `tipo_documento`
    const nombre = p.nombre ?? p.tipo_documento ?? '';
    return {
      prerequisito_id: p.id,
      value: nombre,
      label: nombre,
      required: p.es_obligatorio ?? p.obligatorio ?? true,
      tipo_documento: nombre,
    };
  });
  const documentos_faltantes = [...faltantes, ...extraFaltantes];
  const total = cubiertos.length + documentos_faltantes.length;
  return {
    ...base,
    documentos_faltantes,
    prereq_total: total,
    prereq_covered_count: cubiertos.length,
    prereq_percent: total > 0 ? Math.round((cubiertos.length / total) * 100) : 0,
  };
};

// Encuentra el cliente "Belray" entre los servicios de la persona (por nombre).
export const findBelrayCliente = (clientesServicios: any[]): any | null =>
  (clientesServicios || []).find((c: any) =>
    String(c?.cliente_nombre || c?.nombre || '').toLowerCase().includes('belray')) || null;

// Id numérico del cliente Belray (o null si no aplica).
export const getBelrayClienteId = (clientesServicios: any[]): number | null => {
  const c = findBelrayCliente(clientesServicios);
  if (!c) return null;
  const id = Number(c.cliente_id ?? c.id);
  return id || null;
};
